import type {
  CandidateDiscoverySource,
  Component,
} from '@seek/data-tracking-event-catalogue';
import { useHubble } from '@seek/hubble';
import { useMelwaysInfo } from '@seek/melways-react';

export type CurrentPage = 'people search' | 'profile drawer' | 'public profile';

export const useHubbleClient = () => {
  const hubble = useHubble();
  const { locale } = useMelwaysInfo();

  return {
    trackCandidateProfileDisplayedEvent: ({
      currentPage,
      profileGuid,
      candidateDiscoverySource,
      candidateDiscoverySourceId,
    }: {
      currentPage: CurrentPage;
      profileGuid?: string;
      candidateDiscoverySource: CandidateDiscoverySource;
      candidateDiscoverySourceId?: string;
    }) => {
      hubble.updateOptions({ pageName: currentPage });
      hubble.trackEvent('candidate_profile_displayed', {
        profileGuid,
        candidateDiscoverySource,
        candidateDiscoverySourceId,
      });
    },
    trackLoginEvents: ({
      currentPage,
      currentPageSection,
      component,
      identityProvider,
    }: {
      currentPage: CurrentPage;
      currentPageSection: string;
      component: Component;
      identityProvider?: string;
    }) => {
      hubble.updateOptions({ pageName: currentPage });
      hubble.trackEvent('login_pressed', {
        displayLanguage: locale,
        currentPageSection,
        component,
        identityProvider,
      });
    },
    trackAuthLinkPressedEvents: ({
      currentPage,
      currentPageSection,
      component,
    }: {
      currentPage: CurrentPage;
      currentPageSection: string;
      component: Component;
      identityProvider?: string;
    }) => {
      hubble.updateOptions({ pageName: currentPage });
      hubble.trackEvent('auth_link_pressed', {
        displayLanguage: locale,
        currentPageSection,
        component,
      });
    },
    trackComponentDisplayedEvent: ({
      currentPage,
      currentPageSection,
      component,
    }: {
      currentPage: CurrentPage;
      currentPageSection: string;
      component: Component;
    }) => {
      hubble.updateOptions({ pageName: currentPage });
      hubble.trackEvent('component_displayed', {
        displayLanguage: locale,
        currentPageSection,
        component,
      });
    },
    trackProfileSearchDisplayedEvent: ({
      currentPage,
    }: {
      currentPage: CurrentPage;
    }) => {
      hubble.updateOptions({ pageName: currentPage });
      hubble.trackEvent('profile_search_displayed', {
        displayLanguage: locale,
      });
    },
    trackProfileSearchResultsDisplayedEvent: ({
      currentPage,
      serviceToken,
    }: {
      currentPage: CurrentPage;
      serviceToken: string;
    }) => {
      hubble.updateOptions({ pageName: currentPage });
      hubble.trackEvent('profile_search_results_displayed', {
        displayLanguage: locale,
        serviceToken,
        searchMethod: 'NAME_SEARCH',
      });
    },
  };
};
